import classNames from 'classnames';
import { ReactNode, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import Button from '@alltrails/denali/components/Button';
import Logo from '@alltrails/denali/components/Logo';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { keycodeShortcuts } from '../../hooks/useStyleKeyboardShortcuts';
import useStyleSelection from '../../hooks/useStyleSelection';
import { useAppSelector } from '../../redux';
import { StyleId } from '../../types/Styles';
import adminUrlIsValid from '../../utils/adminUrlIsValid';
import { baseStyleCardConfigs, dynamicStyleCardConfigs, staticStyleCardConfigs } from '../../utils/styleCardConfigs';
import { isBaseStyle, isStaticStyle } from '../../utils/styleIdHelpers';
import styles from './LayerMenuItem.module.scss';

const adminStyleSourceUrlId = 'adminBasemap';

type Props = {
  styleId: StyleId;
  shouldRenderDescription?: boolean;
  onUrlButtonClick?: () => void;
} & Parameters<typeof useStyleSelection>[0];

const LayerMenuItem = ({
  styleId,
  shouldRenderDescription,
  onUrlButtonClick,
  onMapBaseLayerTapped,
  onMapOverlayTapped,
  disableAnalytics,
  handleFailedEntitlementCheck,
  handleRoadblock
}: Props) => {
  const isMobile = useIsMobileSizedScreen();
  const intl = useIntl();
  const { hasPermission } = useAuthorization();
  const isAdmin = hasPermission({ permission: 'trails:manage' });
  const { baseStyleId, dynamicStyleIds, staticStyleIds, adminStyleSettings } = useAppSelector(state => ({
    baseStyleId: state.map.baseStyleId,
    dynamicStyleIds: state.map.dynamicStyleIds,
    staticStyleIds: state.map.staticStyleIds,
    adminStyleSettings: state.map.adminStyleSettings
  }));

  const { onStyleSelect } = useStyleSelection({
    onMapBaseLayerTapped,
    onMapOverlayTapped,
    disableAnalytics,
    handleFailedEntitlementCheck,
    handleRoadblock
  });

  const { active, displayName, description, imgSrc, requiresAdmin } = useMemo<{
    active: boolean;
    displayName: ReactNode;
    description?: ReactNode;
    imgSrc?: string;
    requiresAdmin?: boolean;
  }>(() => {
    if (isBaseStyle(styleId)) {
      const { displayName, description, imgSrc, supports3d, requiresAdmin } = baseStyleCardConfigs[styleId];
      const active = baseStyleId === styleId;
      return { active, displayName, description, imgSrc, supports3d, requiresAdmin };
    } else if (isStaticStyle(styleId)) {
      const { displayName, description, imgSrc, requiresAdmin } = staticStyleCardConfigs[styleId];
      const active = staticStyleIds.includes(styleId);
      return { active, displayName, description, imgSrc, requiresAdmin };
    } else {
      const { displayName, description, imgSrc, requiresAdmin } = dynamicStyleCardConfigs[styleId];
      const active = dynamicStyleIds.includes(styleId);
      return { active, displayName, description, imgSrc, requiresAdmin };
    }
  }, [baseStyleId, dynamicStyleIds, staticStyleIds, styleId]);

  const keyboardShortcutText = useMemo(() => {
    const keycode = keycodeShortcuts[styleId];
    if (keycode && isAdmin && styleId !== 'heatmap') {
      return `Opt+${keycode.replace('Key', '')}`; // KeyA -> A
    }
    return undefined;
  }, [isAdmin, styleId]);

  const isDisabled = useMemo(() => {
    if (isAdmin) {
      if (styleId === adminStyleSourceUrlId && !adminUrlIsValid(adminStyleSettings?.styleSourceUrls?.[adminStyleSourceUrlId!])) {
        return true;
      }
    }
    return false;
  }, [adminStyleSettings?.styleSourceUrls, isAdmin, styleId]);

  const urlButton = useMemo(() => {
    const requiresUrl = styleId === adminStyleSourceUrlId;
    if (requiresUrl && onUrlButtonClick) {
      return (
        <Button
          className={styles.urlButton}
          onClick={onUrlButtonClick}
          size="sm"
          stopPropagation
          testId={`${styleId}-url-button`}
          text={
            !!adminStyleSettings?.styleSourceUrls?.[adminStyleSourceUrlId!] ? (
              <FormattedMessage defaultMessage="Edit custom basemap" />
            ) : (
              <FormattedMessage defaultMessage="Add custom basemap" />
            )
          }
        />
      );
    }
    return null;
  }, [adminStyleSettings, onUrlButtonClick, styleId]);

  const handleStyleSelect = useCallback(() => {
    if (isDisabled) {
      return;
    }
    onStyleSelect(styleId);
  }, [isDisabled, onStyleSelect, styleId]);

  return (
    <div className={styles.adminContainer}>
      <div
        className={classNames(styles.container, { [styles.active]: active, [styles.disabled]: isDisabled })}
        data-testid={styleId}
        onClick={handleStyleSelect}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleStyleSelect();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <Image
          className={styles.image}
          alt={intl.formatMessage({ defaultMessage: 'Preview' })}
          src={imgSrc}
          width={isMobile ? 64 : 48}
          height={isMobile ? 64 : 48}
        />
        <div className={styles.textContainer}>
          <div className={styles.displayNameContainer}>
            <Typography variant="text100bold" className={styles.displayName}>
              {displayName}
            </Typography>
          </div>
          {description && (!isAdmin || shouldRenderDescription) && (
            <Typography variant="text100" color="secondary" className={styles.description}>
              {description}
            </Typography>
          )}
          {!isMobile && keyboardShortcutText && (
            <Typography variant="text100" color="secondary">
              {keyboardShortcutText}
            </Typography>
          )}
        </div>
        {requiresAdmin ? <Logo color="monochrome-dark" size="xs" variant="symbol" /> : null}
      </div>
      {urlButton}
    </div>
  );
};

export default LayerMenuItem;
